/**
 * Automatic Gain Control (AGC) for Speech Normalization
 *
 * RMS-based AGC with two time scales:
 * 1. RMS integration window (slow): measures average loudness over ~1 second
 * 2. Attack/Release smoothing (faster): controls how quickly gain changes
 *
 * Suitable for speech transcription, keeps loudness stable without "pumping"
 */

export interface AGCConfig {
  /** Target RMS loudness (≈ -20 LUFS) */
  targetRMS: number;
  /** Integration window for loudness estimate (seconds) */
  rmsWindowSec: number;
  /** How fast gain rises when too quiet (seconds) */
  attackSec: number;
  /** How fast gain falls when too loud (seconds) */
  releaseSec: number;
  /** Maximum gain multiplier */
  maxGain: number;
  /** Minimum gain multiplier */
  minGain: number;
  /** Silence gate threshold in dBFS (optional, freezes gain when below this) */
  silenceGateDbfs?: number;
}

/**
 * Default AGC configuration optimized for speech
 */
export const DEFAULT_AGC_CONFIG: AGCConfig = {
  targetRMS: 0.05, // ≈ -26 dBFS loudness target (more realistic for microphones)
  rmsWindowSec: 1.0, // Average loudness estimation window
  attackSec: 0.1, // Fast reaction when signal is too quiet
  releaseSec: 1.5, // Slow reaction when signal is too loud
  maxGain: 10.0, // Prevent runaway gain
  minGain: 0.1, // Prevent over-attenuation
  silenceGateDbfs: -70, // Freeze gain below -70 dBFS
};

/**
 * Automatic Gain Control (AGC) Processor
 *
 * Processes audio samples in real-time to normalize speech loudness
 */
export class AudioAGC {
  private config: AGCConfig;
  private sampleRate: number;

  // Derived coefficients
  private rmsAlpha: number;
  private attackAlpha: number;
  private releaseAlpha: number;

  // State variables
  private smoothedRms: number = 0.0;
  private currentGain: number = 1.0;

  // Silence gate threshold (linear)
  private silenceGateThreshold: number;

  constructor(sampleRate: number, config: Partial<AGCConfig> = {}) {
    this.config = { ...DEFAULT_AGC_CONFIG, ...config };
    this.sampleRate = sampleRate;

    // Calculate exponential moving average coefficients
    this.rmsAlpha = Math.exp(-1.0 / (this.config.rmsWindowSec * sampleRate));
    this.attackAlpha = Math.exp(-1.0 / (this.config.attackSec * sampleRate));
    this.releaseAlpha = Math.exp(-1.0 / (this.config.releaseSec * sampleRate));

    // Convert silence gate from dBFS to linear
    this.silenceGateThreshold =
      this.config.silenceGateDbfs !== undefined
        ? Math.pow(10, this.config.silenceGateDbfs / 20)
        : 0;
  }

  /**
   * Process a buffer of audio samples
   * @param input Float32Array of audio samples in range [-1, 1]
   * @returns Float32Array of gain-normalized samples
   */
  process(input: Float32Array): Float32Array {
    const output = new Float32Array(input.length);

    for (let i = 0; i < input.length; i++) {
      const sample = input[i];

      // --- Stage 1: RMS detector (exponential moving average of squared samples)
      this.smoothedRms =
        this.rmsAlpha * this.smoothedRms +
        (1 - this.rmsAlpha) * (sample * sample);
      const measuredRms = Math.sqrt(this.smoothedRms) + 1e-9; // avoid division by zero

      // --- Stage 2: Silence gate (optional)
      // Freeze gain if signal is below silence threshold
      if (
        this.config.silenceGateDbfs !== undefined &&
        measuredRms < this.silenceGateThreshold
      ) {
        // Apply current gain without updating it
        output[i] = sample * this.currentGain;
        continue;
      }

      // --- Stage 3: Desired gain to reach target RMS
      const targetGain = this.config.targetRMS / measuredRms;

      // --- Stage 4: Smooth gain changes with attack/release
      let alpha: number;
      if (targetGain > this.currentGain) {
        // need more gain -> use attack rate (fast)
        alpha = this.attackAlpha;
      } else {
        // need less gain -> use release rate (slow)
        alpha = this.releaseAlpha;
      }

      this.currentGain = alpha * this.currentGain + (1 - alpha) * targetGain;
      this.currentGain = Math.max(
        this.config.minGain,
        Math.min(this.config.maxGain, this.currentGain)
      );

      // --- Stage 5: Apply gain
      output[i] = sample * this.currentGain;
    }

    return output;
  }

  /**
   * Get the current gain being applied (for monitoring/debugging)
   */
  getCurrentGain(): number {
    return this.currentGain;
  }

  /**
   * Get the current measured RMS level (for monitoring/debugging)
   */
  getCurrentRMS(): number {
    return Math.sqrt(this.smoothedRms);
  }

  /**
   * Get the current gain in dB (for monitoring/debugging)
   */
  getCurrentGainDb(): number {
    return 20 * Math.log10(this.currentGain);
  }

  /**
   * Reset the AGC state (useful when starting a new recording)
   */
  reset(): void {
    this.smoothedRms = 0.0;
    this.currentGain = 1.0;
  }

  /**
   * Update configuration dynamically
   */
  updateConfig(config: Partial<AGCConfig>): void {
    this.config = { ...this.config, ...config };

    // Recalculate coefficients
    this.rmsAlpha = Math.exp(
      -1.0 / (this.config.rmsWindowSec * this.sampleRate)
    );
    this.attackAlpha = Math.exp(
      -1.0 / (this.config.attackSec * this.sampleRate)
    );
    this.releaseAlpha = Math.exp(
      -1.0 / (this.config.releaseSec * this.sampleRate)
    );

    // Update silence gate threshold
    this.silenceGateThreshold =
      this.config.silenceGateDbfs !== undefined
        ? Math.pow(10, this.config.silenceGateDbfs / 20)
        : 0;
  }
}
